import Seo from "../components/Seo";
import { useMemo, useState } from "react";
import { AlertCircle, Calculator } from "lucide-react";
import { Slider } from "../components/ui/slider";
import { FEES } from "../lib/site";
import { FadeIn } from "../components/FadeIn";
import PageHeader from "../components/PageHeader";

const ccsPercentage = (income) => {
  if (income <= 85279) return 90;
  if (income >= 535279) return 0;
  return Math.max(0, 90 - Math.floor((income - 85279) / 5000));
};

const money = (n) => `$${n.toFixed(2)}`;

const feeRows = [
  { label: FEES.membership.label, value: `${money(FEES.membership.amount)} per year`, testId: "fee-membership" },
  { label: "Before School Care - permanent", value: `${money(FEES.bsc.permanent)} per session`, testId: "fee-bsc-permanent" },
  { label: "Before School Care - casual", value: `${money(FEES.bsc.casual)} per session`, testId: "fee-bsc-casual" },
  { label: "After School Care - permanent", value: `${money(FEES.asc.permanent)} per session`, testId: "fee-asc-permanent" },
  { label: "After School Care - casual", value: `${money(FEES.asc.casual)} per session`, testId: "fee-asc-casual" },
  { label: "School Vacation Care", value: `${money(FEES.svc.amount)} per session`, testId: "fee-svc" },
  { label: "Excursion levy (applies on excursion days)", value: `${money(FEES.excursionLevy)} on excursion days`, testId: "fee-excursion-levy" },
];

const Fees = () => {
  const [income, setIncome] = useState(150000);

  const estimate = useMemo(() => {
    const pct = ccsPercentage(income);
    const gap = (fee) => fee * (1 - pct / 100);
    return {
      pct,
      bsc: gap(FEES.bsc.permanent),
      asc: gap(FEES.asc.permanent),
      svc: gap(FEES.svc.amount),
    };
  }, [income]);

  return (
    <div data-testid="fees-page">
      <Seo
        title="Fees & Child Care Subsidy (CCS) | Telopea Park OSHC"
        description="Session fees for Before School, After School and Vacation Care at Telopea Park OSHC, plus how the Child Care Subsidy (CCS) reduces your out-of-pocket cost."
        path="/fees"
      />
      <PageHeader
        testId="fees-header"
        eyebrow="Fees & Child Care Subsidy"
        title="Clear fees, with most families receiving CCS"
        description="Our fee schedule applies from August 2026. Most families are eligible for the Child Care Subsidy, which significantly reduces the out-of-pocket cost of care."
      />

      <section className="mx-auto max-w-5xl px-5 sm:px-8 py-16 sm:py-20" data-testid="fees-schedule">
        <FadeIn>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-navy" data-testid="fees-schedule-title">
            Fee schedule - effective {FEES.effective}
          </h2>
          <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
            <table className="w-full text-left" data-testid="fees-table">
              <thead>
                <tr className="bg-navy text-white">
                  <th scope="col" className="px-6 py-4 text-sm font-semibold uppercase tracking-wider">Fee</th>
                  <th scope="col" className="px-6 py-4 text-sm font-semibold uppercase tracking-wider">Amount (before CCS)</th>
                </tr>
              </thead>
              <tbody>
                {feeRows.map((row, i) => (
                  <tr key={row.testId} className={i % 2 === 0 ? "bg-white" : "bg-surface"} data-testid={row.testId}>
                    <td className="px-6 py-4 text-base font-medium text-navy">{row.label}</td>
                    <td className="px-6 py-4 text-base text-slate-600">{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm text-slate-500">
            Fees are reviewed annually by the service&rsquo;s governing body. The annual membership and account keeping fee is
            charged once per family per year.
          </p>
        </FadeIn>
      </section>

      <section className="mx-auto max-w-5xl px-5 sm:px-8 pb-16 sm:pb-20" data-testid="fees-about-ccs">
        <FadeIn>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-navy">About the Child Care Subsidy</h2>
          <div className="mt-6 space-y-5 text-lg text-slate-600 leading-relaxed">
            <p>
              The Child Care Subsidy (CCS) is paid by the Australian Government directly to our service, reducing the
              fees your family is charged. Most families using OSHC receive CCS.
            </p>
            <p>
              To receive CCS, lodge a claim with Services Australia through myGov before or soon after enrolment, and
              confirm your child&rsquo;s enrolment details when prompted. Our team is happy to help families through the
              process.
            </p>
          </div>
        </FadeIn>
      </section>

      <section className="bg-surface border-y border-navy-100" data-testid="fees-estimator">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-16 sm:py-20">
          <FadeIn>
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-waratah-50 text-waratah">
                <Calculator className="h-6 w-6" />
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-navy" data-testid="estimator-title">
                CCS gap estimator
              </h2>
            </div>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed max-w-3xl">
              Slide to your combined household income to estimate how much of the session fee the Child Care Subsidy may
              cover, and what your family might pay.
            </p>

            <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <label htmlFor="household-income-slider" className="text-base font-semibold text-navy">
                  Combined household income (per year)
                </label>
                <span className="font-display text-3xl font-bold text-navy" data-testid="estimator-income-value">
                  ${income.toLocaleString("en-AU")}
                </span>
              </div>
              <Slider
                id="household-income-slider"
                min={40000}
                max={550000}
                step={1000}
                value={[income]}
                onValueChange={([v]) => setIncome(v)}
                aria-label="Combined household income"
                data-testid="estimator-income-slider"
                className="mt-6 [&_[role=slider]]:bg-waratah [&_[role=slider]]:border-waratah [&_[role=slider]]:h-6 [&_[role=slider]]:w-6"
              />
              <div className="mt-2 flex justify-between text-sm text-slate-500">
                <span>$40,000</span>
                <span>$550,000</span>
              </div>

              <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="estimator-results">
                <div className="rounded-xl bg-surface p-5">
                  <p className="text-sm font-semibold uppercase tracking-wider text-slate-500">Estimated CCS</p>
                  <p className="mt-1 font-display text-3xl font-bold text-navy" data-testid="estimator-ccs-percent">
                    {estimate.pct}%
                  </p>
                </div>
                <div className="rounded-xl bg-surface p-5">
                  <p className="text-sm font-semibold uppercase tracking-wider text-slate-500">BSC gap / session</p>
                  <p className="mt-1 font-display text-3xl font-bold text-navy" data-testid="estimator-gap-bsc">
                    {money(estimate.bsc)}
                  </p>
                </div>
                <div className="rounded-xl bg-surface p-5">
                  <p className="text-sm font-semibold uppercase tracking-wider text-slate-500">ASC gap / session</p>
                  <p className="mt-1 font-display text-3xl font-bold text-navy" data-testid="estimator-gap-asc">
                    {money(estimate.asc)}
                  </p>
                </div>
                <div className="rounded-xl bg-surface p-5">
                  <p className="text-sm font-semibold uppercase tracking-wider text-slate-500">SVC gap / session</p>
                  <p className="mt-1 font-display text-3xl font-bold text-navy" data-testid="estimator-gap-svc">
                    {money(estimate.svc)}
                  </p>
                </div>
              </div>

              <div className="mt-8 flex gap-3 rounded-xl border border-waratah-200 bg-waratah-50 p-5" data-testid="estimator-disclaimer">
                <AlertCircle className="h-6 w-6 shrink-0 text-waratah" />
                <p className="text-sm sm:text-base text-navy leading-relaxed">
                  <strong>Estimate only.</strong> This tool provides a general indication based on standard CCS income
                  thresholds and assumes full subsidy hours and that fees fall within the hourly rate cap. It is not an
                  official Services Australia entitlement. Your actual subsidy depends on your family&rsquo;s circumstances,
                  activity test and Centrelink assessment - please check with Services Australia for an official estimate.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

    </div>
  );
};

export default Fees;
